"use client";

import posthog from "posthog-js";

/* ============================================================================
   POSTHOG — ported from v1 Assessment-React (src/utils/posthog.js).

   Same project, same capture settings, same event and property names, so a
   candidate who sits v1 and one who sits v2 land in one funnel while the
   strangler-fig migration runs. Changing any of it here forks the dashboards.

   ENV IS BAKED AT BUILD TIME. NEXT_PUBLIC_* is inlined by `next build` from the
   environment's own env file (see Dockerfile), which is why there is no
   fallback key or host: a wrong default would quietly post this environment's
   candidate telemetry into another environment's project. A missing key warns
   in the console and captures nothing.
   ========================================================================== */

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST;

/** v1's `ENVIRONMENT` super-property, registered on every event so dev, UAT and
 *  production traffic stays separable inside the one shared project. */
const ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT;

type Properties = Record<string, unknown>;

/** Guards the double-init that React strict mode and a remounted provider
 *  would otherwise cause, and lets capture/identify no-op quietly when this
 *  environment has no key rather than logging a warning per event. */
let initialised = false;

/**
 * Analytics must never be able to break a sitting. Every entry point below
 * routes through here: the failure is reported to the console, and the
 * candidate carries on.
 */
function safely(action: string, run: () => void): void {
  try {
    run();
  } catch (error) {
    console.warn(`PostHog ${action} failed:`, error);
  }
}

/**
 * Starts capturing. Called once, on mount, from PostHogInit — never gated on
 * knowing who the candidate is.
 *
 * v1 learned this the hard way: it used to init inside the effect that waited
 * for the signed-in student's profile, so the OTP invite flow — which never
 * loads a profile — initialised nothing and dropped every event an invited
 * candidate produced. v2 is invite-first, so that mistake would cost more here.
 */
export function initPostHog(): void {
  if (typeof window === "undefined" || initialised) return;
  if (!POSTHOG_KEY || !POSTHOG_HOST) {
    console.warn("PostHog API key or host is not provided");
    return;
  }
  initialised = true;
  safely("init", () =>
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      loaded: (loaded) => {
        // Common properties for all events.
        loaded.register({ environment: ENVIRONMENT });
        if (process.env.NODE_ENV !== "production") {
          console.log("PostHog initialized");
        } else {
          loaded.opt_in_capturing();
          loaded.capture("PostHog initialized");
        }
      },
      // The candidate journey is a handful of routes sat once; the events below
      // describe it far better than pageviews or autocapture would, and both
      // would add noise to a screen the candidate cannot leave anyway.
      capture_pageview: false,
      capture_exceptions: true,
      capture_dead_clicks: false,
      autocapture: false,
      capture_heatmaps: false,
      capture_performance: false,
      // Replays are the only way to see a stuck candidate. Inputs are masked —
      // this app's inputs are answers, emails and OTP codes — and a third of
      // sittings is enough to spot a pattern without recording everyone.
      session_recording: { maskAllInputs: true, sampleRate: 0.3 },
    }),
  );
}

/**
 * Names the person behind the anonymous events this session already produced —
 * PostHog back-attributes them, which is how an invite_link_opened captured
 * before sign-in ends up on the candidate who opened it.
 */
export function identifyPostHogUser(email: string | null | undefined, userProperties: Properties = {}): void {
  if (!initialised || !email) return;
  safely("identify", () => posthog.identify(email, { ...userProperties }));
}

export function capturePostHogEvent(eventName: string, eventProperties: Properties = {}): void {
  if (!initialised || !eventName) return;
  safely(`capture ${eventName}`, () => posthog.capture(eventName, eventProperties));
}
